import React, { FunctionComponent, RefObject, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { map, pipe, toPairs, join, find } from 'ramda';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import { IBindingCallback1 } from '../../../../common/models/callback';
import CheckboxList from '../../../../components/CheckboxList';
import { Node } from '../../../../common/models/common/Node';
import CustomPopover from '../../../../components/CustomPopover';
import TextOverflowTooltip from '../../../../components/TextOverflowTooltip';
import Icon from '../../../../components/Icon';
import styles from './styles.module.scss';

interface IProps {
  sendTo: ReferenceType[];
  intl: IntlShape;
  containerRef: RefObject<HTMLElement>;
  onSelect: IBindingCallback1<ReferenceType[]>;
  disabled?: boolean;
}

const ReferenceSelector: FunctionComponent<IProps> = ({ sendTo, onSelect, intl, disabled, containerRef }) => {
  const [selectorVal, setSelectorVal] = useState(sendTo);
  const items = pipe<
    Record<string, ReferenceType>,
    [string, ReferenceType][],
    Node[]
  >(
    toPairs,
    map(([, reference]) => ({
      label: <FormattedMessage id={reference} defaultMessage={reference} />,
      value: reference
    }))
  )(ReferenceType);

  const defaultChecked = map(item => {
    const node = find(({ value }) => item === value, items);
    return node?.value as string;
  }, sendTo);

  const label = pipe<ReferenceType[], string[], string>(
    map(reference => intl.formatMessage({ id: reference, defaultMessage: reference })),
    join(', ')
  )(selectorVal);

  useEffect(() => setSelectorVal(sendTo), [sendTo]);

  const ReferenceList = useCallback(() => {
    useEffect(() => () => setSelectorVal(value => {
      onSelect(value);
      return value;
    }), []);

    return (
      <div className={styles.referencePopup}>
        <CheckboxList
          total={items.length}
          items={items}
          onSelect={references => setSelectorVal(references as ReferenceType[])}
          multipleChoice
          defaultChecked={defaultChecked}
        />
      </div>
    );
  }, [sendTo]);

  const Trigger = () => (
    <div className={`${styles.trigger} popup-trigger ${disabled ? 'trigger-disabled' : ''}`}>
      <div className="d-flex flex-column justify-content-center">
        {selectorVal.length === items.length
          ? <FormattedMessage id="all" defaultMessage="All" />
          : <TextOverflowTooltip content={label} width={200}>{label}</TextOverflowTooltip>}
      </div>
      <Icon icon="chevronDown" className="popup-chevron" />
    </div>
  );

  return (disabled
    ? <Trigger />
    : (
      <CustomPopover
        placement="bottom-start"
        rootClose
        trigger={<Trigger />}
        container={containerRef}
      >
        <ReferenceList />
      </CustomPopover>
    )
  );
};

export default injectIntl(ReferenceSelector);
